import { useEffect, useState } from 'react';
import api from '../api';
import { logger } from '../utils/logger';
import { safeStorage } from '../utils/safeStorage';
import { LANDING_TIMESTAMP_KEY } from '../config/landingGate';

/**
 * Hooks that power the LandingPage hero carousel — extracted out of
 * `LandingPage.js` to keep that file focused on layout & JSX.
 *
 * Three concerns live here:
 *   • `useRotatingTicker`   — the "Updated Xs ago · refreshes every 15s"
 *                              loop plus the demo-set carousel index.
 *   • `useLiveBriefing`     — fetches the user's expiring items when
 *                              authed; null until loaded; empty array =
 *                              "tried & failed" → demo fallback.
 *   • `useWelcomeAutoAdvance` — 30-second countdown that navigates to /
 *                              once it hits zero, but only when `isAuthed`.
 *
 * Every hook owns its own cleanup. None of them touch React Router state
 * directly except `useWelcomeAutoAdvance`, which receives `navigate` as a
 * dependency so the hook stays portable.
 */

/**
 * useRotatingTicker — one `setInterval` drives both the seconds counter
 * (0 → N-1, wrap) and the carousel-set index (incremented on wrap). This
 * keeps the "refreshes every Ns" line aligned with when the set animates.
 *
 * Stamps `polkdesq_landing_last_shown` on mount so the 1h throttle resets
 * whenever a visitor actually sees the carousel (public OR /welcome).
 */
export function useRotatingTicker({ rotationSeconds, setCount }) {
  const [tick, setTick] = useState(0);

  // Stamp the throttle once on mount. Deliberately empty deps — this is
  // a "fire on first paint" effect, identical to the original behaviour.
  useEffect(() => {
    safeStorage.setString(LANDING_TIMESTAMP_KEY, String(Date.now()));
  }, []);

  // Single pure tick counter (PERF-2): secondsAgo and setIndex are DERIVED
  // from it, so no setState-inside-updater impurity.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const secondsAgo = tick % rotationSeconds;
  const setIndex = Math.floor(tick / rotationSeconds) % setCount;
  return { secondsAgo, setIndex };
}

/**
 * useLiveBriefing — fetches the authed user's expiring items from
 * `/welcome/briefing`. Returns `{ liveItems, liveTotals, hasLiveData }`.
 *
 *   • Public visits skip the fetch entirely.
 *   • `liveItems = null` means "not yet loaded" — caller shows demo.
 *   • `liveItems = []` means "loaded but user has nothing" — also demo.
 *   • A populated array means we have live signal — render it.
 */
export function useLiveBriefing(isAuthed) {
  const [liveItems, setLiveItems] = useState(null);
  const [liveTotals, setLiveTotals] = useState(null);

  useEffect(() => {
    if (!isAuthed) return undefined;
    let cancelled = false;
    api.get('/welcome/briefing', { params: { hours: 48, limit: 4 } })
      .then((resp) => {
        if (cancelled) return;
        const items = (resp && resp.data && resp.data.items) || [];
        const totals = (resp && resp.data && resp.data.totals) || null;
        setLiveItems(items);
        setLiveTotals(totals);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.warn(
          'Welcome carousel: live briefing fetch failed, falling back to demo:',
          err?.message,
        );
        setLiveItems([]); // marks "tried & failed" → demo fallback
        setLiveTotals(null);
      });
    return () => { cancelled = true; };
  }, [isAuthed]);

  const hasLiveData = Array.isArray(liveItems) && liveItems.length > 0;
  return { liveItems, liveTotals, hasLiveData };
}

/**
 * useWelcomeAutoAdvance — count DOWN from `seconds` to 0, then navigate
 * to `/`. The visible countdown gives the user agency ("I can click Open
 * Dashboard now" vs "wait, is something stuck?"). No-op when not authed.
 */
export function useWelcomeAutoAdvance({ isAuthed, navigate, seconds }) {
  const [autoAdvanceLeft, setAutoAdvanceLeft] = useState(seconds);

  // Tick the countdown only — the updater stays pure (no navigation/side
  // effects inside setState, which previously triggered a Router update
  // mid-render → "Cannot update a component while rendering a different
  // component" console warning).
  useEffect(() => {
    if (!isAuthed) return undefined;
    const id = setInterval(() => {
      setAutoAdvanceLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [isAuthed]);

  // Navigate as a committed side-effect once the countdown reaches zero.
  useEffect(() => {
    if (!isAuthed) return;
    if (autoAdvanceLeft === 0) {
      navigate('/', { replace: true });
    }
  }, [autoAdvanceLeft, isAuthed, navigate]);

  return autoAdvanceLeft;
}
